/**
 * MapViewQ4Planogram — cuadrante 4 del MapView en modo split.
 *
 * Reemplaza al HealthPanel viejo. Es el plano georreferenciado del piso
 * activo del sitio, navegable (pan + zoom + click), con cámaras y conos
 * reales sobre el footprint del edificio en OSM. Click en una cámara
 * dispara onCameraSelect, que el padre MapView usa para cambiar la cámara
 * activa de Q1 (video + salud).
 *
 * Incidentes activos se renderizan como puntos pulsantes encima de la
 * cámara correspondiente — comando-y-control clásico: el operador ve
 * dónde está el problema sobre el plano y un click lo lleva al video.
 *
 * Selector vertical de pisos a la derecha cuando el sitio es multinivel.
 *
 * Si el sitio aún no tiene planos georreferenciados, mensaje + CTA a
 * SiteConfigView (la georreferenciación es obligatoria pero hasta que el
 * operador lo haga, este cuadrante muestra el placeholder).
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import PlanGeoOverlay from './PlanGeoOverlay'
import FloorSelector  from './FloorSelector'


export default function MapViewQ4Planogram({
  site,
  token,
  selectedCameraId,
  onCameraSelect,
  incidents = [],         // [{id, sensor_uuid?, camera_id?, severity}]
  onOpenConfig,           // () => void cuando el operador quiere ir a SiteConfigView
}) {
  const [floors, setFloors]     = useState([])
  const [cameras, setCameras]   = useState([])
  const [activeFloorId, setActiveFloorId] = useState(null)
  const [imageBlobUrl, setImageBlobUrl]   = useState(null)
  const [loading, setLoading]   = useState(false)
  const [err, setErr]           = useState(null)

  // ── Cargar pisos del sitio ─────────────────────────────────────────────
  useEffect(() => {
    if (!site?.id || !token) return
    setLoading(true); setErr(null)
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch(`/api/site-floor-plans/${site.id}/floors`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) {
          if (r.status === 404) { if (!cancelled) setFloors([]); return }
          throw new Error(`HTTP ${r.status}`)
        }
        const data = await r.json()
        if (cancelled) return
        const arr = Array.isArray(data) ? data : []
        setFloors(arr)
        // Activar piso más bajo (PB o único) por default
        if (arr.length > 0 && !activeFloorId) {
          const sorted = [...arr].sort((a, b) => (a.floor_order ?? 0) - (b.floor_order ?? 0))
          setActiveFloorId(sorted[0].id)
        }
      } catch (e) {
        if (!cancelled) setErr(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [site?.id, token])

  // ── Cargar cámaras del sitio ───────────────────────────────────────────
  useEffect(() => {
    if (!site?.id || !token) return
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch(`/api/cameras/site/${site.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json()
        if (!cancelled) setCameras(Array.isArray(data) ? data : [])
      } catch (e) {
        if (!cancelled) setErr(e.message)
      }
    })()
    return () => { cancelled = true }
  }, [site?.id, token])

  // Cuando el operador selecciona una cámara que está en otro piso, cambiar
  // el piso activo automáticamente (decisión cerrada con Paulo 2026-06-11).
  useEffect(() => {
    if (!selectedCameraId || cameras.length === 0) return
    const cam = cameras.find(c => c.id === selectedCameraId)
    if (cam?.floor_plan?.id && cam.floor_plan.id !== activeFloorId) {
      setActiveFloorId(cam.floor_plan.id)
    }
  }, [selectedCameraId, cameras])
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const activeFloor = useMemo(
    () => floors.find(f => f.id === activeFloorId) || null,
    [floors, activeFloorId],
  )

  // ── Cargar imagen del piso activo ──────────────────────────────────────
  useEffect(() => {
    if (!activeFloor || !token) {
      setImageBlobUrl(null)
      return
    }
    let cancelled = false
    let createdUrl = null
    ;(async () => {
      try {
        const r = await fetch(`/api/site-floor-plans/${activeFloor.id}/image`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const blob = await r.blob()
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setImageBlobUrl(createdUrl)
      } catch (e) {
        if (!cancelled) setErr(`Imagen plano: ${e.message}`)
      }
    })()
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [activeFloor?.id, token])

  // Cámaras del piso activo
  const floorCameras = useMemo(() => {
    if (!activeFloor) return []
    return cameras.filter(c => c.floor_plan?.id === activeFloor.id)
  }, [cameras, activeFloor])

  // IDs de cámaras con incidente activo (para markers pulsantes)
  const incidentCameraIds = useMemo(() => {
    if (!incidents.length) return []
    return incidents
      .filter(i => i.camera_id)
      .map(i => i.camera_id)
  }, [incidents])

  const handleCameraSelect = useCallback((cam) => {
    if (onCameraSelect) onCameraSelect(cam.id)
  }, [onCameraSelect])

  // ── Estados sin contenido ──────────────────────────────────────────────

  if (!site) return null

  // Sin pisos cargados
  if (!loading && floors.length === 0) {
    return (
      <div style={{
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 12, padding: 24,
        background: 'var(--bg-elev-1)',
        color: 'var(--text-muted)', fontSize: 12,
        borderRadius: 8,
      }}>
        <div style={{ fontSize: 32 }}>📐</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          Este sitio aún no tiene plano cargado
        </div>
        <div style={{ textAlign: 'center', maxWidth: 280 }}>
          Cargá los planos por piso desde Configuración del Sitio para verlos
          acá con cámaras georreferenciadas e incidentes en vivo.
        </div>
        {onOpenConfig && (
          <button
            onClick={onOpenConfig}
            style={{
              marginTop: 4,
              padding: '6px 14px',
              background: '#0091ff', color: 'white',
              border: 'none', borderRadius: 4,
              fontSize: 12, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Configurar Sitio →
          </button>
        )}
      </div>
    )
  }

  // Piso activo sin georreferenciar
  if (activeFloor && !activeFloor.is_georeferenced) {
    return (
      <div style={{
        position: 'relative',
        width: '100%', height: '100%',
        background: '#1f2937',
        borderRadius: 8, overflow: 'hidden',
      }}>
        <FloorSelector
          floors={floors}
          activeFloorId={activeFloorId}
          onSelect={setActiveFloorId}
        />
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 12, padding: 24,
          color: 'white', fontSize: 13,
        }}>
          <div style={{ fontSize: 28 }}>📍</div>
          <div style={{ fontWeight: 600 }}>
            Piso "{activeFloor.floor_label || `Orden ${activeFloor.floor_order}`}" sin georreferenciar
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, textAlign: 'center', maxWidth: 320 }}>
            Calzá el plano sobre el mapa real desde Configuración del Sitio para
            que las cámaras aparezcan posicionadas acá.
          </div>
          {onOpenConfig && (
            <button
              onClick={onOpenConfig}
              style={{
                marginTop: 8,
                padding: '6px 14px',
                background: '#f59e0b', color: 'white',
                border: 'none', borderRadius: 4,
                fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Georreferenciar plano →
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── Render principal: plano + cámaras + selector ───────────────────────

  return (
    <div style={{
      position: 'relative',
      width: '100%', height: '100%',
      background: '#1f2937',
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Selector vertical pisos */}
      <FloorSelector
        floors={floors}
        activeFloorId={activeFloorId}
        onSelect={setActiveFloorId}
      />

      {/* Plano + cámaras */}
      {activeFloor?.is_georeferenced && imageBlobUrl && (
        <PlanGeoOverlay
          mode="navigate"
          floorPlan={activeFloor}
          imageUrl={imageBlobUrl}
          cameras={floorCameras}
          selectedCameraId={selectedCameraId}
          incidentCameraIds={incidentCameraIds}
          initialCenter={
            activeFloor.georeference
              ? {
                  lat: (activeFloor.georeference.nw[0] + activeFloor.georeference.se[0]) / 2,
                  lon: (activeFloor.georeference.nw[1] + activeFloor.georeference.se[1]) / 2,
                }
              : undefined
          }
          height="100%"
          onCameraSelect={handleCameraSelect}
        />
      )}

      {/* Indicador de piso activo abajo a la izquierda */}
      {activeFloor && (
        <div style={{
          position: 'absolute', bottom: 12, left: 12,
          padding: '4px 10px',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          zIndex: 11,
          pointerEvents: 'none',
        }}>
          {activeFloor.floor_label || `Piso ${activeFloor.floor_order}`}
          {floorCameras.length > 0 && (
            <span style={{ opacity: 0.7, marginLeft: 8 }}>
              {floorCameras.length} cám
            </span>
          )}
        </div>
      )}

      {err && (
        <div style={{
          position: 'absolute', top: 12, left: 12,
          padding: '6px 10px',
          background: 'rgba(217, 48, 37, 0.95)',
          color: 'white', borderRadius: 4,
          fontSize: 11, zIndex: 11,
        }}>
          {err}
        </div>
      )}
    </div>
  )
}
